// FINANCE FORK: obsidian + violet dark theme. Every value in this file is a
// deliberate fork choice — on upstream rebases keep our values wholesale and
// only adopt upstream's *new* token names (see docs/rebase-strategy.md).
//
// System: slate surfaces (page slate-950, cards slate-900, hover/borders
// slate-800, separators slate-700), slate text ramp (100/400/500), violet-500
// accent, indigo-400 secondary accent. Color economy: accents + grayscale
// everywhere; rose-400/emerald-400 are reserved for negative/positive money
// comparisons. Semantic notice/warning/error tokens keep the upstream
// green/orange/red palette. See docs/design.md for intent.
import * as colorPalette from '#style/palette';

// Local ramp — tailwind slate/violet/indigo values. Kept local (not in
// #style/palette) because the palette module is upstream rebase surface.
const slate950 = '#020617';
const slate900 = '#0f172a';
const slate800 = '#1e293b';
const slate700 = '#334155';
const slate600 = '#475569';
const slate500 = '#64748b';
const slate400 = '#94a3b8';
const slate300 = '#cbd5e1';
const slate200 = '#e2e8f0';
const slate100 = '#f1f5f9';
const slate50 = '#f8fafc';
const violet950 = '#2e1065';
const violet900 = '#4c1d95';
const violet700 = '#6d28d9';
const violet600 = '#7c3aed';
const violet500 = '#8b5cf6';
const violet400 = '#a78bfa';
const violet300 = '#c4b5fd';
const violet200 = '#ddd6fe';
const violet100 = '#ede9fe';
const indigo950 = '#1e1b4b';
const indigo600 = '#4f46e5';
const indigo500 = '#6366f1';
const indigo400 = '#818cf8';
const emerald400 = '#34d399';
const rose400 = '#fb7185';

export const pageBackground = slate950;
export const pageBackgroundModalActive = slate900;
export const pageBackgroundTopLeft = slate950;
export const pageBackgroundBottomRight = slate950;
export const pageBackgroundLineTop = violet500;
export const pageBackgroundLineMid = slate950;
export const pageBackgroundLineBottom = slate900;
export const pageText = slate100;
export const pageTextLight = slate400;
// Subdued text is slate-400, not slate-500 — slate-500 falls below WCAG AA
// (3.75:1) on slate-900 cards/menus. slate-500 stays in use for decorative
// and inactive states only.
export const pageTextSubdued = slate400;
export const pageTextDark = slate50;
export const pageTextPositive = violet400;
export const pageTextLink = violet500;
export const pageTextLinkLight = violet400;

export const cardBackground = slate900;
export const cardBorder = slate800;
export const cardShadow = 'rgba(0, 0, 0, 0.4)';

export const tableBackground = slate900;
export const tableRowBackgroundHover = slate800;
export const tableText = slate100;
export const tableTextLight = slate400;
export const tableTextSubdued = slate400; // AA on slate-900 (see pageTextSubdued)
export const tableTextSelected = slate50;
export const tableTextHover = slate50;
export const tableTextInactive = slate500;
export const tableHeaderText = slate400;
export const tableHeaderBackground = slate950;
export const tableBorder = slate800;
export const tableBorderSelected = violet500;
export const tableBorderHover = slate700;
export const tableBorderSeparator = slate700;
export const tableRowBackgroundHighlight = indigo950; // accent tint
export const tableRowBackgroundHighlightText = slate50;
export const tableRowHeaderBackground = slate950;
export const tableRowHeaderText = slate50;

export const numberPositive = emerald400;
export const numberNegative = rose400;
export const numberNeutral = slate500;
export const budgetNumberNegative = numberNegative;
export const budgetNumberZero = tableTextSubdued;
export const budgetNumberNeutral = tableText;
export const budgetNumberPositive = budgetNumberNeutral;
export const templateNumberFunded = numberPositive;
export const templateNumberUnderFunded = colorPalette.orange300;
export const toBudgetPositive = numberPositive; // also the Sankey Surplus band
export const toBudgetZero = numberPositive;
export const toBudgetNegative = budgetNumberNegative; // also the Sankey Deficit band

export const sidebarBackground = slate950;
export const sidebarItemBackgroundPending = colorPalette.orange200;
export const sidebarItemBackgroundPositive = colorPalette.green500;
export const sidebarItemBackgroundFailed = colorPalette.red300;
export const sidebarItemAccentSelected = violet500;
export const sidebarItemBackgroundHover = slate900;
export const sidebarItemText = slate400;
export const sidebarItemTextSelected = slate100;
export const sidebarBudgetName = slate400;

export const menuBackground = slate900;
export const menuItemBackground = slate900;
export const menuItemBackgroundHover = slate800;
export const menuItemText = slate100;
export const menuItemTextHover = slate50;
export const menuItemTextSelected = violet400;
export const menuItemTextHeader = violet400;
export const menuBorder = slate800;
export const menuBorderHover = violet500;
export const menuKeybindingText = slate400; // 10px text needs AA on slate-900
export const menuAutoCompleteBackground = slate900;
export const menuAutoCompleteBackgroundHover = slate800;
export const menuAutoCompleteText = slate300;
export const menuAutoCompleteTextHeader = violet400;
export const menuAutoCompleteItemText = menuItemText;

export const modalBackground = slate900;
export const modalBorder = slate700;
export const mobileHeaderBackground = slate900;
export const mobileHeaderText = slate100;
export const mobileHeaderTextSubdued = slate400;
export const mobileHeaderTextHover = 'rgba(200, 200, 200, .15)';
export const mobilePageBackground = slate950;
export const mobileNavBackground = slate900;
export const mobileNavItem = slate400;
export const mobileNavItemSelected = violet400;
export const mobileAccountShadow = cardShadow;
export const mobileAccountText = slate300;
export const mobileTransactionSelected = violet400;

// Mobile view themes (for the top bar)
export const mobileViewTheme = mobileHeaderBackground;
export const mobileConfigServerViewTheme = violet700;

export const markdownNormal = violet900;
export const markdownDark = violet600;
export const markdownLight = violet950;

// Button
export const buttonMenuText = slate300;
export const buttonMenuTextHover = buttonMenuText;
export const buttonMenuBackground = 'transparent';
export const buttonMenuBackgroundHover = 'rgba(200, 200, 200, .25)';
export const buttonMenuBorder = slate600;
export const buttonMenuSelectedText = slate50;
export const buttonMenuSelectedTextHover = slate50;
export const buttonMenuSelectedBackground = violet600;
export const buttonMenuSelectedBackgroundHover = violet500;
export const buttonMenuSelectedBorder = buttonMenuSelectedBackground;

export const buttonPrimaryText = colorPalette.white;
export const buttonPrimaryTextHover = buttonPrimaryText;
export const buttonPrimaryBackground = violet500;
export const buttonPrimaryBackgroundHover = violet400;
export const buttonPrimaryBorder = buttonPrimaryBackground;
export const buttonPrimaryShadow = 'rgba(0, 0, 0, 0.6)';
export const buttonPrimaryDisabledText = slate500;
export const buttonPrimaryDisabledBackground = slate800;
export const buttonPrimaryDisabledBorder = buttonPrimaryDisabledBackground;

export const buttonNormalText = slate200;
export const buttonNormalTextHover = slate100;
export const buttonNormalBackground = slate800;
export const buttonNormalBackgroundHover = slate700;
export const buttonNormalBorder = slate700;
export const buttonNormalShadow = 'rgba(0, 0, 0, 0.4)';
export const buttonNormalSelectedText = colorPalette.white;
export const buttonNormalSelectedBackground = violet600;
export const buttonNormalDisabledText = slate500;
export const buttonNormalDisabledBackground = slate900;
export const buttonNormalDisabledBorder = slate700;

export const calendarText = slate100;
export const calendarBackground = slate950;
export const calendarItemText = slate300;
export const calendarItemBackground = slate800;
export const calendarSelectedBackground = buttonNormalSelectedBackground;

export const buttonBareText = buttonNormalText;
export const buttonBareTextHover = buttonNormalText;
export const buttonBareBackground = 'transparent';
export const buttonBareBackgroundHover = 'rgba(200, 200, 200, .3)';
export const buttonBareBackgroundActive = 'rgba(200, 200, 200, .5)';
export const buttonBareDisabledText = buttonNormalDisabledText;
export const buttonBareDisabledBackground = buttonBareBackground;

export const noticeBackground = colorPalette.green800;
export const noticeBackgroundLight = colorPalette.green900;
export const noticeBackgroundDark = colorPalette.green500;
export const noticeText = colorPalette.green300;
export const noticeTextLight = colorPalette.green500;
export const noticeTextDark = colorPalette.green150;
export const noticeTextMenu = colorPalette.green500;
export const noticeBorder = colorPalette.green800;
export const warningBackground = colorPalette.orange800;
export const warningText = colorPalette.orange300;
export const warningTextLight = colorPalette.orange500;
export const warningTextDark = colorPalette.orange100;
export const warningBorder = colorPalette.orange500;
export const errorBackground = colorPalette.red800;
export const errorText = colorPalette.red200;
export const errorTextDark = colorPalette.red150;
export const errorTextDarker = errorTextDark;
export const errorTextMenu = colorPalette.red200;
export const errorBorder = colorPalette.red500;
export const upcomingBackground = violet900;
export const upcomingText = violet200;
export const upcomingBorder = tableBorder;

export const formLabelText = violet300;
export const formLabelBackground = indigo950;
export const formInputBackground = slate900;
export const formInputBackgroundSelected = slate800;
export const formInputBackgroundSelection = violet500;
export const formInputBorder = slate700;
export const formInputTextReadOnlySelection = slate900;
export const formInputBorderSelected = violet500;
export const formInputText = slate200;
export const formInputTextSelected = slate50;
export const formInputTextPlaceholder = slate500;
export const formInputTextPlaceholderSelected = slate400;
export const formInputTextSelection = slate900;
export const formInputShadowSelected = 'rgba(139, 92, 246, 0.4)'; // violet500
export const formInputTextHighlight = violet500;
export const checkboxText = tableText;
export const checkboxBackgroundSelected = violet500;
export const checkboxBorderSelected = violet500;
export const checkboxShadowSelected = violet700;
export const checkboxToggleBackground = slate700;
export const checkboxToggleBackgroundSelected = violet500;
export const checkboxToggleDisabled = slate600;

export const pillBackground = slate800;
export const pillBackgroundLight = slate900;
export const pillText = slate300;
export const pillTextHighlighted = violet300;
export const pillBorder = slate700;
export const pillBorderDark = pillBorder;
export const pillBackgroundSelected = violet600;
export const pillTextSelected = slate50;
export const pillBorderSelected = violet500;
export const pillTextSubdued = slate500;

export const reportsRed = numberNegative;
export const reportsBlue = indigo400;
export const reportsGreen = numberPositive;
export const reportsGray = slate500;
export const reportsLabel = pageText;
// Inner chart labels sit on chartQual-colored segments of varying
// brightness — white clears AA on all of them; slate-900 fails on violet-500.
export const reportsInnerLabel = colorPalette.white;
export const reportsNumberPositive = numberPositive;
export const reportsNumberNegative = numberNegative;
export const reportsNumberNeutral = numberNeutral;
export const reportsChartFill = violet500;

export const noteTagBackground = violet900;
export const noteTagBackgroundHover = violet700;
export const noteTagDefault = noteTagBackground;
export const noteTagText = violet100;

export const budgetOtherMonth = slate950;
export const budgetCurrentMonth = tableBackground;
export const budgetHeaderOtherMonth = slate800;
export const budgetHeaderCurrentMonth = tableHeaderBackground;

export const floatingActionBarBackground = slate900;
export const floatingActionBarBorder = slate700;
export const floatingActionBarText = slate100;

export const tooltipText = slate200;
export const tooltipBackground = slate900;
export const tooltipBorder = slate700;

export const calendarCellBackground = slate950;

export const overlayBackground = 'rgba(0, 0, 0, 0.4)';

// Chart colors — qualitative scale (9 colors), violet/indigo/slate ramp.
// Adjacent entries alternate hue and lightness so neighboring Sankey bands
// and stacked series stay distinguishable without leaving the accent system.
export const chartQual1 = violet500;
export const chartQual2 = indigo400;
export const chartQual3 = violet300;
export const chartQual4 = indigo500;
export const chartQual5 = violet400;
export const chartQual6 = slate400;
export const chartQual7 = violet600;
export const chartQual8 = slate500;
export const chartQual9 = indigo600;
